<template>
  <div v-if="hasSecurityRequirements" class="row">
    <div>
      <api-header :id="id" tag="h4">{{ $t('documentation.api.auth.title') }}</api-header>

      <div v-if="hasBasicScheme && hasJWTBearerScheme">
        <p v-html="$t('documentation.api.auth.bearerSummary[0]')" />
        <p v-html="$t('documentation.api.auth.bearerSummary[1]')" />
      </div>

      <div v-if="oauth2Schemes.length > 0">
        <table
          v-for="scheme in oauth2Schemes"
          :key="scheme.name"
          :aria-describedby="$t('documentation.api.auth.table.caption')"
          data-cy="oauth2SchemesTable"
        >
          <thead>
            <tr>
              <th colspan="2">
                <i18n-t keypath="documentation.api.auth.oauth2.schemeTitle" tag="span" scope="global">
                  <template #name>
                    {{ scheme.name }}
                  </template>
                </i18n-t>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="scheme.description">
              <html-text tag="td" colspan="2" :html="scheme.description" />
            </tr>

            <ApiSecurityOAuth2FlowData v-for="(data, type) in scheme.flows" :key="type" :type="type" :flow="data" />
          </tbody>
        </table>
      </div>

      <table v-if="otherSchemes.length > 0" :aria-describedby="$t('documentation.api.auth.table.caption')">
        <thead>
          <tr>
            <th>{{ $t('documentation.api.auth.table.key') }}</th>
            <th>{{ $t('documentation.api.auth.table.description') }}</th>
            <th>{{ $t('documentation.api.auth.table.where') }}</th>
            <th>{{ $t('documentation.api.auth.table.example') }}</th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="hasOAuthBearerScheme">
            <td>
              <b>Authorization</b>
            </td>
            <td>
              <p>
                {{ $t('documentation.api.auth.table.oauth.summary') }}
                <br />
                <small
                  ><link-wrapper href="https://vbcdeveloper.vonage.com/getting-started/create-an-access-token">{{
                    $t('documentation.api.auth.table.oauth.link')
                  }}</link-wrapper></small
                >
              </p>
            </td>
            <td>
              <p>Headers</p>
            </td>
            <td>
              <code>Bearer &lt;access_token&gt;</code>
            </td>
          </tr>

          <tr v-if="hasJWTBearerScheme">
            <td>
              <b>Authorization</b>
            </td>
            <td>
              <p>
                {{ $t('documentation.api.auth.table.jwt.summary') }}
                <br />
                <small
                  ><link-wrapper href="/concepts/guides/authentication#json-web-tokens-jwt">{{
                    $t('documentation.api.auth.table.jwt.link')
                  }}</link-wrapper></small
                >
              </p>
            </td>
            <td><p>Headers</p></td>
            <td><code>Bearer &lt;JWT&gt;</code></td>
          </tr>

          <tr v-if="hasBasicScheme">
            <td>
              <b>Authorization</b>
            </td>
            <td>
              <p>
                {{ $t('documentation.api.auth.table.basic.summary') }}
                <br />
                <small
                  ><link-wrapper
                    href="/concepts/guides/authentication#header-based-api-key-and-secret-authentication"
                    >{{ $t('documentation.api.auth.table.basic.link') }}</link-wrapper
                  ></small
                >
              </p>
            </td>
            <td><p>Headers</p></td>
            <td><code>Basic &lt;base64&gt;</code></td>
          </tr>

          <tr v-for="value in dynamicValues" :key="value.name">
            <td>
              <b>{{ value.name }}</b>
            </td>
            <html-text tag="td" :html="value.description" />
            <td>
              <p class="capitalize">{{ value.where }}</p>
            </td>
            <td>
              <code v-if="value.where === 'query'">?{{ value.name }}=&lt;{{ value.name }}&gt;</code>
              <code v-else-if="value.where">&lt;{{ value.name }}&gt;</code>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { SecuritySchemeObject } from '@bifrost-dev-docs-mfe/common/types/apiDocs';
import ApiHeader from '@/components/api/content/common/ApiHeader.vue';
import HtmlText from '@/components/utils/HtmlText.vue';
import LinkWrapper from '@/components/markdown/widgets/LinkWrapper.vue';
import ApiSecurityOAuth2FlowData from '@/components/api/content/operations/security/ApiSecurityOAuth2FlowData.vue';

const props = defineProps({
  id: { type: String, required: true },
  security: { type: Object as PropType<SecuritySchemeObject>, required: true },
});

const hasSecurityRequirements = computed(() => Object.keys(props.security).length > 0);
const hasBasicScheme = computed(() => 'basic' in props.security);
const hasJWTBearerScheme = computed(() => 'bearer:JWT' in props.security);
const hasOAuthBearerScheme = computed(() => 'bearer:OAuth' in props.security);
const oauth2Schemes = computed(() =>
  Object.entries(props.security)
    .filter(([id]) => id.startsWith('oauth2'))
    .map(([, value]) => value)
);
const otherSchemes = computed(() =>
  Object.entries(props.security)
    .filter(([id]) => !id.startsWith('oauth2'))
    .map(([, value]) => value)
);

const dynamicValues = computed(() => Object.values(otherSchemes.value).filter(obj => Object.keys(obj).length > 0));
</script>
